import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, gravarToken, lerToken, type Usuario } from '@/lib/api';

interface ContextoAuth {
  usuario: Usuario | null;
  carregando: boolean;
  entrar: (email: string, senha: string) => Promise<void>;
  registrar: (dados: { nome: string; email: string; senha: string; telefone?: string }) => Promise<void>;
  /** Salva nome e telefone da conta e devolve o usuário já atualizado. */
  salvarPerfil: (dados: { nome?: string; telefone?: string | null }) => Promise<void>;
  sair: () => void;
  recarregar: () => Promise<void>;
}

const Contexto = createContext<ContextoAuth | null>(null);

export function ProvedorAuth({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [carregando, setCarregando] = useState(true);

  const recarregar = useCallback(async () => {
    if (!lerToken()) {
      setUsuario(null);
      setCarregando(false);
      return;
    }
    try {
      const perfil = await api.perfil();
      setUsuario(perfil);
    } catch {
      gravarToken(null);
      setUsuario(null);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  const entrar = useCallback(async (email: string, senha: string) => {
    const resposta = await api.entrar({ email, senha });
    gravarToken(resposta.token);
    setUsuario(resposta.usuario);
  }, []);

  const registrar = useCallback(
    async (dados: { nome: string; email: string; senha: string; telefone?: string }) => {
      const resposta = await api.registrar(dados);
      gravarToken(resposta.token);
      setUsuario(resposta.usuario);
    },
    [],
  );

  const salvarPerfil = useCallback(async (dados: { nome?: string; telefone?: string | null }) => {
    const atualizado = await api.atualizarPerfil(dados);
    // A resposta traz só os campos da conta; preservamos o resto do perfil.
    setUsuario((anterior) => ({ ...anterior, ...atualizado }));
  }, []);

  const sair = useCallback(() => {
    gravarToken(null);
    setUsuario(null);
  }, []);

  const valor = useMemo<ContextoAuth>(
    () => ({ usuario, carregando, entrar, registrar, salvarPerfil, sair, recarregar }),
    [usuario, carregando, entrar, registrar, salvarPerfil, sair, recarregar],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useAuth(): ContextoAuth {
  const contexto = useContext(Contexto);
  if (!contexto) throw new Error('useAuth precisa estar dentro de <ProvedorAuth>.');
  return contexto;
}
